import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector,
  useDeserifiedSelector } from '../../../reduxStore/reduxHooks';
import { Box, SxProps, Typography, useMediaQuery } from '@mui/material';

import {
  ActionsPopup,
  ChapterSelectedCard,
  ChipBadge,
  ContentLockPopup,
  IStyles,
  ResourceRejectedPopup,
  IconWrapper,
  Loader,
  LockToggleButton,
  NoContentCard,
  SelectTopicPopup,
  cloneObject,
  deepClone,
  deserify,
  findDownloadedSubjectByParams,
  getEnumKeyByEnumValue,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
  useDownloadedSubjectRequests,
  useFeatureEnabled,
  TopicCardPrime,
} from '@geneo2-web/shared-ui';
import {
  ResourceInfo,
  SubjectChapterMiniInfo,
  TopicMiniInfoV2,
} from '@protos/content_management/content.common.apis_pb';
import {
  ApprovalInfo,
  ChapterMetaInfo,
  ContentApprovalStatusEnum,
  DownloadedSubject,
  Module_ModuleCategoryEnum,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { ContentLockModuleData } from '@protos/learning_management/lms.common.apis_pb';
import {
  ContentLockStatusType,
  Feature,
} from '@protos/school_management/school.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { TeacherLoginResponseType } from '@protos/user_management/ums.login.apis_pb';
import { DownloadButtonWrapper } from '../../../components/DownloadButtonWrapper';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import {
  LESSON_PLAN_LISTS,
  RESOURCE_UPLOAD,
  TEACH_CHAPTER_SELECTION,
} from '../../../routeHandling/RoutesNomenclature';
import { interactionEvent } from '../../Auth/auth.events';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  setChapterResources,
  setChapterTopicInfo,
  setCustomResourceData,
  setSelectedTopic,
} from '../reducer/teach.slice';
import { aiChapterCloseEvent, aiChapterOpenEvent } from '../teach.events';
import { CustomUploadFileResponse } from '../../Assessment/reducer/assessmentFlow.slice';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { NcfMappingPopup } from './components/NcfMappingPopup';
import {
  ChapterPerformanceStats,
  LearningOutcomes,
  NCFGoal,
} from '@protos/analysis_management/analysis.teacher.apis_pb';
import { LearningOutcomePopup } from './components/LearningOutcomePopup';
import { RightPanelTeachTopicSelection } from './components/RightPanelTeachTopicSelection';
import { getRoundedDownValue } from '../../../utils/functions';

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    backgroundColor: 'neutral.paleGrey',
    padding: {
      md: `${pxTovW(14)} ${pxToRem(20)}`,
      lg: `${pxTovW(14)} ${pxTovW(240)}`,
    },
    paddingTop: { md: pxTovW(40) },
  },
  bodyBox: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: pxTovW(25),
  },
  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(14), md: pxTovW(10) },
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(20)} ${pxToRem(5)} ${pxToRem(20)}`,
      md: 0,
    },
  },
  chapterBox: {
    padding: { xs: pxToRem(20), md: 0 },
    paddingBottom: { xs: pxToRem(20), md: pxTovW(46) },
  },
  resourceContainer: {
    flexBasis: { xs: '100%', md: 1, lg: pxTovW(862) },
    marginBottom: { xs: pxToRem(43), md: 0 },
    // backgroundColor: 'blue',
  },
  chooseTopicBox: {
    // flexBasis: { xs: '100%', md: '40%', lg: pxTovW(554) },
    display: 'flex',
    flexDirection: 'column',
    // gap: { xs: pxToRem(25) },
    gap: { xs: pxToRem(7), md: pxTovW(18) },
  },
  topicLoader: { width: { xs: '100%', md: pxTovW(1200), lg: pxTovW(940) } },
  topicBox: {
    width: { xs: '100%', md: pxTovW(1200), lg: pxTovW(940) },
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    '& > div': { borderBottom: '1px solid #cdc1ff' },
  },
};

interface FetchFuncArg {
  subjectId: string;
  chapterId: string;
}

interface IlockedPopupData {
  resourceId?: number | string;
  image: string;
  noofTopics: string;
  title: string;
  moduleId?: number;
  category?: Module_ModuleCategoryEnum;
  lockedStatus: ContentLockStatusType;
}

export default function TeachTopicSelection() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setSelectedFunction } = useGlobalContext();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const commonServiceClientContext = useCommonServiceClientContext();
  const {
    ContentCommonAPIServiceV1ClientWithStatusCodeHandler,
    TeacherUploadContentAPIServiceV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV1ClientWithStatusCodeHandler,
    ContentCommonAPIServiceV2ClientWithStatusCodeHandler,
    TeacherAnalysisAPIServiceV2ClientWithStatusCodeHandler,
  } = commonServiceClientContext;

  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { subject_chapter_info, chapter_resources, subject_topic_info } =
    deserify(useAppSelector((state) => state.teach));
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );

  const [selectTopicPopup, setSelectTopicPopup] = useState(false);
  const [ncfMappingPopup, setNcfMappingPopup] = useState(false);
  const [learningOutcomePopup, setLearningOutcomePopup] = useState(false);
  const [analysisdata, setAnalysisData] = useState<
    | {
        chapterPerformance: ChapterPerformanceStats;
        learningOutcomes: LearningOutcomes[];
      }
    | undefined
  >();
  const [ncfData, setncfData] = useState<NCFGoal[]>();
  const [topicLoading, setTopicLoading] = useState<
    'loading' | 'completed' | 'error'
  >('completed');
  const [resourceLoading, setResourceLoading] = useState<
    'loading' | 'completed' | 'error'
  >('completed');
  const [isContentLockPopupOpen, setIsContentLockPopupOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rejectedResourcePopupOpen, setRejectedResourcePopupOpen] =
    useState(false);
  const [approvalInfo, setApprovalInfo] = useState<ApprovalInfo>();

  const [lockedPopupData, setLockedPopData] = useState<IlockedPopupData | null>(
    null
  );
  const [learningOutcomesData, setLearningOutcomesData] = useState<{
    title: string;
    LearningOutcomes: LearningOutcomes[];
  }>();
  const [contentLockData, setContentLockData] =
    useState<ContentLockModuleData>();
  const teacher_id = getLocalStorage('userId');
  const { subject_id, chapter_id } = useParams();

  const [confirmDeletePopup, setConfirmDeletePopup] = useState(false);
  interface DataForDelete {
    resourceId: string;
    resourceTitle: string;
  }
  const [selectedDataForDelete, setSelectedDataForDelete] = useState<
    DataForDelete | undefined
  >();

  const topicNavigation = async (topicInfo: TopicMiniInfoV2) => {
    await interactionEvent({
      context: 'Choose_a_topic',
      name: 'VIEW',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
    dispatch(setSelectedTopic(topicInfo));
    navigate(
      `${LESSON_PLAN_LISTS}/${subject_id}/${chapter_id}/${topicInfo.topicId}`
    );
  };

  const handleOpenPopup = (
    ev?: React.MouseEvent,
    topicData?: TopicMiniInfoV2,
    categories?: ResourceInfo
  ) => {
    ev?.stopPropagation();
    if (topicData && categories) {
      console.log(
        'Both topicData and categories are provided. Please provide only one.'
      );
      return;
    }
    if (topicData) {
      setLockedPopData({
        image: findSelectedChapterData().image, // Assuming topicData contains an image property
        noofTopics: topicData.topicStats?.lessonPlanCount
          ? String(topicData.topicStats?.lessonPlanCount)
          : '',
        title: topicData?.topicTitle || '',
        moduleId: topicData.topicId,
        category: Module_ModuleCategoryEnum.MODULE_CATEGORY_TOPIC,
        lockedStatus:
          getContentLockedData(topicData.topicId, contentLockData)
            ?.lockStatus ||
          ContentLockStatusType.CONTENT_LOCK_STATUS_IS_UNLOCKED,
      });
      setIsContentLockPopupOpen(true);
    } else if (categories) {
      setLockedPopData({
        resourceId: categories.resourceId,
        image: categories.posterImageUrl
          ? getMediaBasePath(
              categories.posterImageUrl,
              categories.resourceCategoryType ===
                ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                ? 'userDataBucket'
                : 'processedMediaBucket'
            )
          : '',
        noofTopics: '',
        title: categories.title || '',
        moduleId: Number(chapter_id),
        category: Module_ModuleCategoryEnum.MODULE_CATEGORY_CHAPTER,
        lockedStatus: getCategoryLockedStatus(
          categories.resourceId,
          contentLockData
        ),
      });

      setIsContentLockPopupOpen(true);
    } else {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Something went wrong',
          open: true,
        })
      );
    }
  };

  const handleClosePopup = () => {
    setIsContentLockPopupOpen(false);
    setLockedPopData(null);
  };

  // Getting Data for ChapterSelectedCard
  const findSelectedChapterData = () => {
    const selectedChapter = subject_chapter_info?.chapters.find(
      (chapter: SubjectChapterMiniInfo) =>
        chapter_id && +chapter_id === chapter.chapterId
    );
    if (selectedChapter) {
      return {
        name: selectedChapter.chapterTitle,
        image: getMediaBasePath(
          selectedChapter.posterImagesUrl,
          'processedMediaBucket'
        ),
      };
    }
    return { name: '', image: '' };
  };

  async function fetchChapterTopicInfo({ subjectId, chapterId }: FetchFuncArg) {
    try {
      setTopicLoading('loading');

      const response =
        await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterTopicInfoV2(
          {
            personId: Number(teacher_id),
            // personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            personType: 'TEACHER',
            subjectId: Number(subjectId),
            chapterId: Number(chapterId),
            sectionId: class_subject_info?.sectionId,
            isAssessFlow: false,
            bookId: class_subject_info?.bookId,
          }
        );
      if (response) {
        if (isContentLockFeature) {
          await getLessonLockedStatus(teacher_id);
        }
        setTopicLoading('completed');
        if (response?.data) {
          const data = response.data;
          await aiChapterOpenEvent({
            chapterId: Number(chapter_id),
            chapterSessionId: uuidv4(),
            isOffline: isOffline,
          });
          data.topics.sort((a, b) => a.topicNo - b.topicNo);
          dispatch(setChapterTopicInfo(data));
        }
      }
    } catch (err) {
      setTopicLoading('error');
      console.log(err);
    }
  }

  async function fetchChapterResources({ subjectId, chapterId }: FetchFuncArg) {
    try {
      // setLoading(true);
      setResourceLoading('loading');

      const response =
        await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterResources(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId: Number(subjectId),
            chapterId: Number(chapterId),
            sectionId: class_subject_info?.sectionId,
          }
        );
      if (response) {
        // ! it is already fetched in the fetchChapterTopicInfo function
        // if (isContentLockFeature && contentLockData) {
        //   await getLessonLockedStatus(teacher_id);
        // }
        setResourceLoading('completed');
        if (response?.data) {
          const data = response.data;
          dispatch(setChapterResources(data));
        }
      }

      // setLoading(false);
    } catch (err) {
      setResourceLoading('error');
      // setLoading(false);
      // setError(err);
      console.log(err);
    }
  }

  const getAnalysisData = async ({ subjectId, chapterId }: FetchFuncArg) => {
    try {
      const response =
        await TeacherAnalysisAPIServiceV2ClientWithStatusCodeHandler.getChapterAnalysis(
          {
            teacherId: BigInt(teacher_id),
            subjectId: Number(subjectId),
            chapterId: Number(chapterId),
            sectionId: class_subject_info?.sectionId,
          }
        );
      if (response.chapterPerformance && response.learningOutcomes) {
        setAnalysisData({
          chapterPerformance: response.chapterPerformance,
          learningOutcomes: response.learningOutcomes,
        });
      }
      if (response.ncfMapping) {
        setncfData(response.ncfMapping);
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  const isContentLockFeature = useFeatureEnabled(
    featureInfo,
    Feature.CONTENT_LOCK
  );
  const teachClassSubjects = user_info?.teachClassSubjects.find(
    (classData) =>
      class_subject_info?.classname === classData.className &&
      class_subject_info?.section === classData.sectionName
  );
  const selectedSubject = teachClassSubjects
    ? teachClassSubjects.subjects.find(
        (subjectData) => subjectData.subjectId === Number(subject_id)
      )
    : undefined;

  const [fetchingState, setFetchingState] = useState(false);
  const downloadedSubject = useDownloadedSubjectRequests({
    subjectId: Number(subject_id),
  });
  const [downloadedMetaData, setDownloadedMetaData] = useState<
    Record<number, ChapterMetaInfo | undefined>
  >({});

  const editResource = async (editProps: {
    resourceId: string;
    coverTitle: string;
  }) => {
    try {
      const response =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchResourceEditDetails(
          {
            personId: BigInt(teacher_id),
            resourceId: editProps.resourceId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
          }
        );
      console.log('response:', response);
      if (response) {
        dispatch(
          setCustomResourceData({
            resourceId: editProps.resourceId,
            selected_chapter: {
              chapter_name: findSelectedChapterData().name,
              chapter_id: Number(chapter_id),
            },
            resourceTitle: response.coverTitle || editProps.coverTitle,
            resourceTime: response.estimatedTimeInMin,
            // contentLock: response. ,
            custom_resource_cover_image: response.coverImageInfo
              ? createCustomUploadFileResponse([response.coverImageInfo])[0]
              : undefined,
            custom_resource_uploaded_files: createCustomUploadFileResponse(
              response.filesInfo
            ),
          })
        );

        navigate(`${RESOURCE_UPLOAD}/${editProps.resourceId}`);
      }
      console.log('response:', response);
    } catch (error) {
      console.log('error:', error);
      dispatch(
        setToastInfo({
          label: `Error Occurred! While trying to get the data.`,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const createCustomUploadFileResponse = (
    cloudUploadData: CloudUpload[]
  ): CustomUploadFileResponse[] => {
    return cloudUploadData.map((data) => {
      return {
        uploadFileData: data,
        fileUploadStatus: 'success',
        fileSize: (data.fileSizeInMb || 0) * (1024 * 1024),
      };
    });
  };

  const deleteResource = async () => {
    try {
      const response =
        await TeacherUploadContentAPIServiceV1ClientWithStatusCodeHandler.deleteTeacherContent(
          {
            teacherId: BigInt(teacher_id),
            resourceId: selectedDataForDelete?.resourceId,
          }
        );

      if (response) {
        dispatch(
          setToastInfo({
            label: `${selectedDataForDelete?.resourceTitle} deleted successfully`,
            variant: 'info',
            open: true,
          })
        );

        window.location.reload();
      }
    } catch (error) {
      console.log('error:', error);

      dispatch(
        setToastInfo({
          label: `Error Occurred! Unable to delete the resource.`,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // const getLessonLockedStatus = async (teacherId: string) => {
  //   try {
  //     const response =
  //       await LmsCommonAPIServiceV1ClientWithStatusCodeHandler.moduleLockInfoFetch(
  //         {
  //           teacherId: BigInt(teacherId),
  //           subjectId: class_subject_info?.subjectId,
  //           schoolClassSectionId: class_subject_info?.sectionId,
  //         }
  //       );
  //     const data = response?.data;
  //     if (data) {
  //       dispatch(setContentLock(data));
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  const handleLockedRadioChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = getEnumKeyByEnumValue(
      ContentLockStatusType,
      Number(event.target.value)
    );
    const currentData = cloneObject(lockedPopupData);
    if (val && currentData) {
      currentData.lockedStatus = ContentLockStatusType[val];
      setLockedPopData(currentData);
    }
    await interactionEvent({
      context: 'Choose_state',
      name: val === 'CONTENT_LOCK_STATUS_IS_LOCKED' ? 'LOCKED' : 'UNLOCKED',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  const handleSubmit = async (ev?: React.MouseEvent) => {
    ev?.stopPropagation();
    try {
      // Ensure essential values are present before making the API call
      if (!lockedPopupData?.moduleId || !lockedPopupData?.category) {
        throw new Error('Module ID and category must be provided');
      }

      // Build the moduleInfo object dynamically, including resourceId only if it exists
      const moduleInfo = {
        moduleId: lockedPopupData?.moduleId,
        category: lockedPopupData?.category,
        lockStatus: lockedPopupData.lockedStatus,
        ...(lockedPopupData?.resourceId && {
          resourceId: String(lockedPopupData.resourceId),
        }),
      };

      console.log('Module info:', moduleInfo);

      // Perform the API call
      const response =
        await LmsCommonAPIServiceV1ClientWithStatusCodeHandler.teacherContentLockUpdate(
          {
            teacherId: BigInt(teacher_id),
            subjectId: class_subject_info?.subjectId,
            schoolClassSectionId: class_subject_info?.sectionId,
            moduleInfo,
          }
        );

      // Check if the response is valid and handle accordingly
      if (response) {
        console.log('API response:', response);
        await getLessonLockedStatus(teacher_id); // Refresh lesson lock status
        handleClosePopup(); // Close the popup upon successful submission
        setLockedPopData(null); // Clear the lockedPopupData state
        dispatch(
          setToastInfo({
            label: 'Lock status updated successfully',
            variant: 'success',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'Failed to update lock status',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      // Enhanced error handling
      console.error('Submission error:', error);

      // Display a user-friendly error message
      dispatch(
        setToastInfo({
          label: 'Something went wrong',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const getLessonLockedStatus = async (teacherId: string) => {
    try {
      const response =
        await LmsCommonAPIServiceV1ClientWithStatusCodeHandler.moduleLockInfoFetch(
          {
            teacherId: BigInt(teacherId),
            subjectId: class_subject_info?.subjectId,
            schoolClassSectionId: class_subject_info?.sectionId,
            moduleId: Number(chapter_id),
            category: Module_ModuleCategoryEnum.MODULE_CATEGORY_CHAPTER,
          }
        );
      const data = response?.data;
      if (data) {
        setContentLockData(data);
        // dispatch(setContentLock(data));
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  // -----------------------------------------------------

  useEffect(() => {
    if (
      subject_id &&
      chapter_id &&
      !isNaN(Number(subject_id)) &&
      !isNaN(Number(chapter_id))
    ) {
      (async () => {
        setFetchingState(true);
        const indexDbData = await findDownloadedSubjectByParams({
          subjectId: Number(subject_id),
        });
        const meta = deepClone(downloadedMetaData);
        if (indexDbData.length > 0) {
          const data = indexDbData[0];
          const offlineData =
            await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.getOfflineMetaInfo(
              {
                chapterId: Number(chapter_id),
              }
            );
          meta[Number(chapter_id)] = offlineData.data;
        }
        setDownloadedMetaData(meta);
        setFetchingState(false);
      })();
    }
  }, [subject_id, chapter_id]);

  // -----------------------------------------------------

  // useEffect(() => {
  //   const fetchLessonLockedStatus = async () => {
  //     setIsSubmitted(false);
  //     try {
  //       if (isContentLockFeature) {
  //         await getLessonLockedStatus(teacher_id);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching lesson lock status:', error);
  //       // Handle the error if necessary
  //     }
  //   };

  //   fetchLessonLockedStatus();
  // }, [isSubmitted]);

  useEffect(() => {
    if (subject_id && chapter_id) {
      fetchChapterTopicInfo({ subjectId: subject_id, chapterId: chapter_id });

      fetchChapterResources({ subjectId: subject_id, chapterId: chapter_id });
      getAnalysisData({ subjectId: subject_id, chapterId: chapter_id });
    }
  }, [chapter_id]);

  useEffect(() => {
    // Back button functionality
    const redirectToTeachDashboard = async () => {
      await aiChapterCloseEvent({
        chapterId: Number(chapter_id),
        isOffline: isOffline,
      });
      navigate(`${TEACH_CHAPTER_SELECTION}/${subject_id}`);
    };
    setSelectedFunction(() => redirectToTeachDashboard);
    dispatch(setCustomResourceData(undefined));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // -----------------------------------------------------

  if (topicLoading === 'loading')
    return (
      <Box sx={styles.topicLoader}>
        <Loader />
      </Box>
    );

  return (
    <Box sx={styles.root}>
      <Box sx={styles.chapterBox}>
        <ChapterSelectedCard
          image={findSelectedChapterData().image}
          chapterName={
            findSelectedChapterData().name ? findSelectedChapterData().name : ''
          }
          className={
            class_subject_info
              ? class_subject_info?.classname + class_subject_info?.section
              : ''
          }
          subject={class_subject_info ? class_subject_info?.subject : ''}
          headingRightElement={
            isMobile ? null : (
              <DataDisplayContainer
                totalLessons={subject_topic_info?.chapterStats?.lessonPlanCount}
                totalResources={subject_topic_info?.chapterStats?.resourceCount}
              />
            )
          }
        />
        {isMobile && (
          <DataDisplayContainer
            totalLessons={subject_topic_info?.chapterStats?.lessonPlanCount}
            totalResources={subject_topic_info?.chapterStats?.resourceCount}
          />
        )}
      </Box>

      <Box sx={styles.bodyBox}>
        <Box
          sx={{
            ...styles.chooseTopicBox,
            flexBasis: {
              xs: '100%',
              md:
                chapter_resources?.categoryResourceMap.length === 0
                  ? '100%'
                  : '40%',
              lg:
                chapter_resources?.categoryResourceMap.length === 0
                  ? '100%'
                  : pxTovW(554),
            },
          }}
        >
          <Box sx={styles.textWithBadge}>
            <Typography variant="h2">Choose a Topic</Typography>
            <Typography variant="h3">
              <ChipBadge
                label={subject_topic_info?.topics?.length}
                color="primary"
                size="small"
                sx={{ display: { xs: 'none', md: 'unset' } }}
              />
            </Typography>
          </Box>

          {
            // topicLoading === 'loading' ? (
            //   <Box sx={styles.topicLoader}>
            //     <Loader />
            //   </Box>
            // ) :

            topicLoading === 'error' ? (
              <Box sx={styles.topicLoader}>
                <NoContentCard
                  variant="error"
                  icon="error"
                  text="Error Occurred"
                />
              </Box>
            ) : subject_topic_info ? (
              <Box sx={styles.topicBox}>
                {subject_topic_info.topics?.length === 0 ? (
                  <Box>
                    <NoContentCard
                      variant="white"
                      icon="coming-soon-yellow"
                      text="No topics to show"
                    />
                  </Box>
                ) : (
                  subject_topic_info?.topics?.map((topics, index) => {
                    return (
                      <TopicCardPrime
                        key={index}
                        title={topics.topicTitle}
                        lessonPlanCount={topics.topicStats?.lessonPlanCount}
                        resourcesCount={topics.topicStats?.resourceCount}
                        viewAllClickHandler={() => topicNavigation(topics)}
                        rootStyle={{
                          paddingX: { xs: pxToRem(20), md: pxTovW(28) },
                          paddingY: { xs: pxToRem(11), md: pxTovW(36) },
                        }}
                        isLocked={
                          isContentLockFeature &&
                          contentLockData &&
                          getContentLockedData(topics.topicId, contentLockData)
                            ?.lockStatus ===
                            ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
                            ? true
                            : false
                        }
                      >
                        {!fetchingState && (
                          <Box
                            sx={{
                              marginLeft: '5px',
                              display: 'flex',
                              // marginTop: '10px',
                              gap: { xs: pxToRem(5), md: pxTovW(5) },
                            }}
                          >
                            {isContentLockFeature && (
                              <LockToggleButton
                                isLocked={
                                  contentLockData
                                    ? getContentLockedData(
                                        topics.topicId,
                                        contentLockData
                                      )?.lockStatus
                                    : false
                                }
                                handleOpenPopup={async (ev) => {
                                  handleOpenPopup(ev, topics);
                                  await interactionEvent({
                                    context: 'Topic',
                                    name: 'CHANGE_LOCK_STATUS',
                                    pathSegments: pathSegments,
                                    isOffline: isOffline,
                                  });
                                }}
                              />
                            )}
                            <DownloadButtonWrapper
                              user={new TeacherLoginResponseType(user_info)}
                              downloadedSubject={
                                downloadedSubject
                                  ? new DownloadedSubject(downloadedSubject)
                                  : undefined
                              }
                              downloadedMetaData={downloadedMetaData}
                              setDownloadedMetaData={setDownloadedMetaData}
                              subjectId={Number(subject_id)}
                              classId={class_subject_info?.classId}
                              sectionId={class_subject_info?.sectionId}
                              chapterId={Number(chapter_id)}
                              topicId={topics.topicId}
                              title={`Topic - ${topics.topicTitle}`}
                              interactionEventUrl={
                                'Teacher_teach_topics_selection'
                              }
                              interactionEventContext={'topic'}
                            />
                          </Box>
                        )}
                      </TopicCardPrime>
                    );
                  })
                )}
              </Box>
            ) : (
              <Box sx={styles.topicLoader}>
                <NoContentCard
                  variant="white"
                  icon="coming-soon-yellow"
                  text="No topics to show"
                  rootStyle={{
                    flexDirection: 'column',
                    gap: { xs: pxToRem(20), md: pxTovW(20) },
                  }}
                />
              </Box>
            )
          }
        </Box>

        {/*  R I G H T  -  P A N E L  */}
        <RightPanelTeachTopicSelection
          analysisdata={analysisdata}
          ncfData={ncfData}
          setSelectTopicPopup={setSelectTopicPopup}
          setNcfMappingPopup={setNcfMappingPopup}
          setLearningOutcomesData={setLearningOutcomesData}
          setLearningOutcomePopup={setLearningOutcomePopup}
          resourceLoading={resourceLoading}
          setRejectedResourcePopupOpen={setRejectedResourcePopupOpen}
          setSelectedDataForDelete={setSelectedDataForDelete}
          setApprovalInfo={setApprovalInfo}
          isContentLockFeature={isContentLockFeature}
          contentLockData={contentLockData}
          handleOpenPopup={handleOpenPopup}
          editResource={editResource}
          setConfirmDeletePopup={setConfirmDeletePopup}
        />
      </Box>

      <ActionsPopup
        open={confirmDeletePopup}
        handleClose={() => {
          setConfirmDeletePopup(false);
        }}
        fontSmall
        iconName="delete"
        popupText={`Are you sure you want to delete - ${selectedDataForDelete?.resourceTitle}?`}
        yesClickHandler={async () => {
          deleteResource();
          setConfirmDeletePopup(false);
        }}
        noClickHandler={async () => {
          setConfirmDeletePopup(false);
        }}
      />

      {isContentLockFeature && lockedPopupData && (
        <ContentLockPopup
          open={isContentLockPopupOpen}
          onClose={handleClosePopup}
          onSubmit={async (ev?: React.MouseEvent) => {
            lockedPopupData && handleSubmit(ev);
            await interactionEvent({
              context:
                lockedPopupData.lockedStatus ===
                ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
                  ? 'Locked'
                  : 'Unlocked',
              name: 'SUBMIT',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          }}
          selectedValue={lockedPopupData.lockedStatus}
          onRadioChange={handleLockedRadioChange}
          lockDataObj={lockedPopupData}
          subtitle="Lesson Plan"
        />
      )}

      {approvalInfo?.approvalStatus ===
        ContentApprovalStatusEnum.CONTENT_APPROVAL_STATUS_REJECTED && (
        <ResourceRejectedPopup
          open={rejectedResourcePopupOpen}
          handleClose={() => {
            setRejectedResourcePopupOpen(false);
          }}
          fontSmall
          iconName="exclamationerror"
          popupText={`Are you sure you want to delete this resource?`}
          tryAgainClickHandler={async () => {
            setRejectedResourcePopupOpen(false);
            if (selectedDataForDelete) {
              editResource({
                resourceId: selectedDataForDelete?.resourceId,
                coverTitle: selectedDataForDelete?.resourceTitle,
              });
            }
          }}
          removeClickHandler={async () => {
            setRejectedResourcePopupOpen(false);
            setConfirmDeletePopup(true);
          }}
          approvalInfo={approvalInfo}
        />
      )}

      <SelectTopicPopup
        outsideCloseButton
        modalState={selectTopicPopup}
        setModalState={setSelectTopicPopup}
        cardData={analysisdata?.chapterPerformance.topicsPerformance
          .sort((a, b) => a.topicId - b.topicId)
          .map((e) => {
            return {
              topicName: e.topicTitle,
              topicImage: getMediaBasePath(
                e.posterImageUrl,
                'processedMediaBucket'
              ),
              score: Math.round(e.scorePercent),
            };
          })}
        chapterName={analysisdata?.chapterPerformance.chapterTitle || ''}
        chapterImage={analysisdata?.chapterPerformance?.posterImageUrl || ''}
      />

      <NcfMappingPopup
        modalState={ncfMappingPopup}
        ncfMapping={ncfData}
        setModalState={setNcfMappingPopup}
      />

      <LearningOutcomePopup
        open={learningOutcomePopup}
        onClose={() => {
          setLearningOutcomePopup(false);
        }}
        title={learningOutcomesData?.title || ''}
        learningOutcomes={learningOutcomesData?.LearningOutcomes || []}
      />
    </Box>
  );
}

const DataDisplayContainer = (props: {
  totalLessons?: number;
  totalResources?: number;
}) => {
  const { totalLessons, totalResources } = props;
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        gap: { xs: pxToRem(10), md: pxTovW(20) },
      }}
    >
      {totalLessons ? (
        <PillDataCard
          iconName="phone-book"
          primaryBgColor="#FFF5CB"
          secondaryBgColor="#FDDD53"
          dataText={getRoundedDownValue(totalLessons)}
          dataLabel="Lesson Plans Available"
        />
      ) : null}

      {totalResources ? (
        <PillDataCard
          iconName="papers"
          primaryBgColor="#F3F0FF"
          secondaryBgColor="#B5A7FF"
          dataText={getRoundedDownValue(totalResources)}
          dataLabel="Resources Available"
        />
      ) : null}
    </Box>
  );
};

interface IPillDataCardProps {
  iconName: string;
  primaryBgColor: string;
  secondaryBgColor: string;
  dataText: string;
  dataLabel: string;
  iconSx?: SxProps;
  rootSx?: SxProps;
  circleSx?: SxProps;
}
const PillDataCard = (props: IPillDataCardProps) => {
  const {
    iconName,
    primaryBgColor,
    secondaryBgColor,
    dataText,
    dataLabel,
    iconSx,
    rootSx,
    circleSx,
  } = props;

  return (
    <Box
      sx={{
        backgroundColor: primaryBgColor,
        boxSizing: 'border-box',
        borderRadius: {
          xs: `${pxToRem(40)} ${pxToRem(10)} ${pxToRem(10)} ${pxToRem(40)}`,
          md: `${pxTovW(50)} ${pxTovW(16)} ${pxTovW(16)} ${pxTovW(50)}`,
        },
        width: { xs: pxToRem(160), md: pxTovW(300) },
        display: 'flex',
        gap: { xs: pxToRem(9), md: pxTovW(15) },
        alignItems: 'center',
        mt: { xs: pxToRem(20), md: pxTovW(0) },
        border: `1px solid ${secondaryBgColor}`,

        ...rootSx,
      }}
    >
      <Box
        sx={{
          backgroundColor: secondaryBgColor,
          boxSizing: 'border-box',
          borderRadius: '50%',
          width: { xs: pxToRem(45), md: pxTovW(90) },
          height: { xs: pxToRem(45), md: pxTovW(90) },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          ...circleSx,
        }}
      >
        <IconWrapper
          name={iconName}
          size="large"
          parentFolder="icons"
          type="png"
          customSx={iconSx}
        />
      </Box>

      <Box>
        <Typography
          variant="h4"
          color="neutral.royalBlue"
          sx={{
            fontWeight: 1000,
            fontSize: { xs: pxToRem(18), md: pxTovW(37) },
          }}
        >
          {dataText}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Poppins',
            fontSize: { xs: pxToRem(9), md: pxTovW(16) },
          }}
        >
          {dataLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const getContentLockedData = (
  topic_id: number,
  contentLockData?: ContentLockModuleData
) => {
  return contentLockData?.moduleUpdatedLockInfo?.children.find(
    (val) => val.moduleId === topic_id
  );
};

const getCategoryLockedStatus = (
  resourceId: string,
  contentLockData?: ContentLockModuleData
) => {
  return contentLockData?.moduleUpdatedLockInfo?.lockedResourceIds?.includes(
    resourceId
  )
    ? ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
    : ContentLockStatusType.CONTENT_LOCK_STATUS_IS_UNLOCKED;
};
